import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LifeBuoy, X, Paperclip, Camera, FileText, Image as ImageIcon } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { api } from '../../utils/api';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // mirror backend MAX_ATTACHMENT_BYTES
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

const fmtSize = (n: number) =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

export default function SupportReportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [attachWarning, setAttachWarning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const location = useLocation();
  const params = useParams();

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCategory('bug');
    setMessage('');
    setBusy(false);
    setError(null);
    setSuccess(false);
    setAttachments([]);
    setCapturing(false);
    setAttachWarning(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setError(`"${f.name}" is larger than 10 MB and was skipped.`);
        continue;
      }
      next.push(f);
    }
    if (next.length) setAttachments((prev) => [...prev, ...next]);
  };

  const removeAttachment = (idx: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // Capture the page the reporter is looking at. The modal is unmounted first
  // (isOpen stays true, but the overlay is hidden while `capturing`) so the
  // snapshot is of the underlying page, not of this dialog.
  const captureScreenshot = async () => {
    setError(null);
    setCapturing(true);
    try {
      await new Promise((r) => setTimeout(r, 80)); // let the overlay unpaint
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, { logging: false, useCORS: true, scale: 1 });
      const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
      if (!blob) throw new Error('no blob');
      const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setError('The screenshot is larger than 10 MB and was not attached.');
      } else {
        setAttachments((prev) => [...prev, file]);
      }
    } catch {
      setError('Could not capture a screenshot of this page.');
    } finally {
      setCapturing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setAttachWarning(false);
    const route = location.pathname;
    const caseId = params.caseId || params.reportId || params.claimId || params.id;
    try {
      const res: any = await api.post('/api/v1/support-tickets/report', {
        message: trimmed,
        category,
        route,
        ...(caseId ? { case_id: caseId } : {}),
      });
      const ticketId = res?.id;
      if (ticketId && attachments.length) {
        for (const file of attachments) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            await api.upload(`/api/v1/support-tickets/${ticketId}/attachments`, fd);
          } catch {
            // The report itself is already filed; a failed attachment is a soft
            // warning, not a lost report.
            setAttachWarning(true);
          }
        }
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send report. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = !!message.trim() && !busy;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors hover:text-[var(--color-text-secondary)]"
        style={{
          background: 'transparent',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text-muted)',
        }}
        aria-label="Report an issue"
        data-testid="report-issue-button"
      >
        <LifeBuoy className="w-4 h-4" />
        <span>Report</span>
      </button>

      {isOpen && !capturing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (e.currentTarget === e.target) handleClose();
          }}
        >
          <div
            className="relative w-full rounded p-5"
            style={{
              maxWidth: '400px',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute"
              style={{ top: '16px', right: '16px', color: 'var(--color-text-muted)' }}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {!success ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div
                    className="font-mono text-[10px] uppercase tracking-wide"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    SEND FEEDBACK
                  </div>
                  <h2 className="cs-h2 mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    Report an issue
                  </h2>
                </div>

                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value)}
                    className="w-full rounded px-3 py-2 text-sm outline-none focus:outline-none focus:border-[var(--color-text-muted)] appearance-none"
                    style={{
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <option value="bug">Bug</option>
                    <option value="question">Question</option>
                    <option value="data_issue">Data issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Details
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                    placeholder="What happened? What did you expect?"
                    className="w-full rounded px-3 py-2 text-sm outline-none focus:outline-none focus:border-[var(--color-text-muted)] resize-none"
                    style={{
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                </div>

                {/* Attachments */}
                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Attachments <span style={{ opacity: 0.6 }}>(optional)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors hover:text-[var(--color-text-primary)]"
                      style={{ background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      Attach file
                    </button>
                    <button
                      type="button"
                      onClick={captureScreenshot}
                      className="inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors hover:text-[var(--color-text-primary)]"
                      style={{ background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Capture screenshot
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPT}
                      multiple
                      className="hidden"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        addFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </div>

                  {attachments.length > 0 && (
                    <>
                      <ul className="mt-3 space-y-1">
                        {attachments.map((f, i) => {
                          const isImg = f.type.startsWith('image/');
                          return (
                            <li
                              key={`${f.name}-${i}`}
                              // Flat surface fill, no border — reads as a data row, not a
                              // third ghost button (K3: borders should mean "clickable action").
                              className="flex items-center gap-2 rounded px-2 py-1 text-xs"
                              style={{ background: 'var(--color-bg-elevated)' }}
                            >
                              {isImg ? (
                                <ImageIcon className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                              ) : (
                                <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                              )}
                              <span className="truncate" style={{ color: 'var(--color-text-secondary)' }} title={f.name}>
                                {f.name}
                              </span>
                              <span className="ml-auto font-mono shrink-0" style={{ color: 'var(--color-text-dim)' }}>
                                {fmtSize(f.size)}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeAttachment(i)}
                                aria-label={`Remove ${f.name}`}
                                className="shrink-0 transition-colors hover:text-[var(--color-text-primary)]"
                                style={{ color: 'var(--color-text-muted)' }}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      <p className="mt-1.5 text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                        Screenshots capture exactly what is on your screen now — including any
                        case or financial data. They are visible only to platform administrators.
                      </p>
                    </>
                  )}
                </div>

                {error && (
                  <div className="text-sm" style={{ color: 'var(--color-status-adverse)' }}>
                    {error}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-sm font-medium transition-colors hover:text-[var(--color-text-primary)]"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                    style={
                      canSubmit
                        ? {
                            background: 'var(--color-brand)',
                            color: 'var(--color-on-accent)',
                          }
                        : {
                            background: 'transparent',
                            border: '1px solid var(--color-text-muted)',
                            color: 'var(--color-text-muted)',
                          }
                    }
                    onMouseEnter={(e) => {
                      if (canSubmit) {
                        e.currentTarget.style.background = 'var(--color-brand-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canSubmit) {
                        e.currentTarget.style.background = 'var(--color-brand)';
                      }
                    }}
                  >
                    {busy ? 'SENDING…' : 'Send report'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>
                  Thanks — we got it
                </h2>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Your report was sent to the team.
                </p>
                {attachWarning && (
                  <p className="text-sm" style={{ color: 'var(--color-status-adverse)' }}>
                    One or more attachments could not be uploaded, but your message was sent.
                  </p>
                )}
                <div className="flex items-center justify-end pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                    style={{
                      background: 'var(--color-brand)',
                      color: 'var(--color-on-accent)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--color-brand-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--color-brand)';
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
